import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Trash2, Plus, Loader2 } from 'lucide-react';

type Item = { id: string; name: string; category: string | null; active: boolean };

export default function AdminCatalog() {
  const { toast } = useToast();
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [filter, setFilter] = useState('');

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from('grocery_catalog').select('*').order('name');
    setItems((data as Item[]) || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim()) return;
    const { error } = await supabase.from('grocery_catalog').insert({ name: name.trim(), category: category.trim() || null });
    if (error) toast({ title: 'Failed', description: error.message, variant: 'destructive' });
    else { setName(''); setCategory(''); toast({ title: 'Item added' }); load(); }
  };

  const toggle = async (id: string, active: boolean) => {
    await supabase.from('grocery_catalog').update({ active }).eq('id', id);
    setItems((prev) => prev.map((x) => x.id === id ? { ...x, active } : x));
  };

  const remove = async (id: string) => {
    await supabase.from('grocery_catalog').delete().eq('id', id);
    toast({ title: 'Item removed' });
    load();
  };

  const filtered = items.filter((i) => i.name.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">Grocery Catalog ({items.length})</h2>
      <div className="rounded-lg border bg-card p-4 space-y-3">
        <h3 className="font-medium text-sm">Add new item</h3>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input placeholder="Item name (e.g., Amul milk)" value={name} onChange={(e) => setName(e.target.value)} />
          <Input placeholder="Category (optional)" value={category} onChange={(e) => setCategory(e.target.value)} className="sm:w-48" />
          <Button onClick={add}><Plus className="h-4 w-4 mr-1" />Add</Button>
        </div>
      </div>

      <Input placeholder="Filter items..." value={filter} onChange={(e) => setFilter(e.target.value)} />

      <div className="rounded-lg border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50"><tr className="text-left"><th className="p-3">Name</th><th className="p-3">Category</th><th className="p-3">Active</th><th className="p-3 text-right">Actions</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="p-6 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading...</td></tr>
            ) : filtered.map((i) => (
              <tr key={i.id} className="border-t">
                <td className="p-3 font-medium">{i.name}</td>
                <td className="p-3 text-muted-foreground">{i.category || '—'}</td>
                <td className="p-3"><Switch checked={i.active} onCheckedChange={(v) => toggle(i.id, v)} /></td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => remove(i.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}